import React, { useMemo, useState } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import StyledDataGrid from 'shared/components/StyledDataGrid';
import CreativeThumbnail from '../components/CreativeThumbnail';
import TagEditorDialog from './TagEditorDialog';
import {
  FIELD_LABELS,
  humanize,
  TaggedCreative,
  TagField,
  VocabValue,
} from './types';

interface Props {
  rows: TaggedCreative[];
  vocabulary: VocabValue[];
  onSetTag: (
    creativeKey: string,
    field: TagField,
    value: string | string[] | null,
  ) => Promise<void>;
}

function Toolbar() {
  return (
    <GridToolbarContainer sx={{ px: 1, py: 1 }}>
      <GridToolbarQuickFilter placeholder="Search tags, personas, products…" />
    </GridToolbarContainer>
  );
}

// A tag cell: the value, or a muted dash; a small indigo dot when a person
// corrected it (so a reviewer can see what the AI said vs. what was fixed).
function TagCell({
  text,
  edited,
  muted,
}: {
  text: string;
  edited: boolean;
  muted?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      {edited && (
        <Tooltip title="Corrected by a person">
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#6366F1',
              flexShrink: 0,
            }}
          />
        </Tooltip>
      )}
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontSize: '0.8125rem',
          color: muted || !text ? '#94A3B8' : '#0F172A',
        }}
        title={text}
      >
        {text || '—'}
      </Typography>
    </Box>
  );
}

// Every tagged creative with its effective tags. Click a row to see what the AI
// saw and correct any tag.
export default function CreativeTagsTable({
  rows,
  vocabulary,
  onSetTag,
}: Props) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const labelOf = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const v of vocabulary)
      byKey.set(`${v.dimension}:${v.product || ''}:${v.value_key}`, v.label);
    return (
      dimension: 'persona' | 'selling_point',
      product: string,
      key: string | null,
    ) =>
      key
        ? byKey.get(
            `${dimension}:${dimension === 'persona' ? '' : product}:${key}`,
          ) || key
        : '';
  }, [vocabulary]);

  const tagColumn = (
    field: TagField,
    width: number,
    text: (row: TaggedCreative) => string,
  ): GridColDef => ({
    field,
    headerName: FIELD_LABELS[field],
    width,
    valueGetter: params => text(params.row as TaggedCreative),
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row as TaggedCreative;
      return (
        <TagCell
          text={String(params.value || '')}
          edited={row.overridden.includes(field)}
        />
      );
    },
  });

  const columns: GridColDef[] = [
    {
      field: 'thumb',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <CreativeThumbnail
          src={(params.row as TaggedCreative).thumb_proxy_url}
        />
      ),
    },
    { field: 'product', headerName: 'Product', width: 150 },
    {
      field: 'media_type',
      headerName: 'Type',
      width: 80,
      valueFormatter: p => humanize(p.value as string),
    },
    {
      field: 'spend',
      headerName: 'Lifetime spend',
      width: 120,
      type: 'number',
      valueFormatter: p =>
        p.value == null
          ? ''
          : `$${Math.round(p.value as number).toLocaleString()}`,
    },
    tagColumn('persona', 200, r =>
      labelOf('persona', r.product, r.tags.persona),
    ),
    tagColumn('lead_selling_point', 200, r =>
      labelOf('selling_point', r.product, r.tags.lead_selling_point),
    ),
    tagColumn('supporting_selling_points', 220, r =>
      (r.tags.supporting_selling_points || [])
        .map(k => labelOf('selling_point', r.product, k))
        .join(', '),
    ),
    tagColumn('hook_device', 130, r => humanize(r.tags.hook_device)),
    tagColumn('persuasion_angle', 120, r => humanize(r.tags.persuasion_angle)),
    tagColumn('format', 130, r => humanize(r.tags.format)),
    tagColumn('presenter', 130, r => humanize(r.tags.presenter)),
    tagColumn('offer_type', 110, r => humanize(r.tags.offer_type)),
    tagColumn('awareness_level', 120, r => humanize(r.tags.awareness_level)),
    {
      field: 'ai_persona',
      headerName: "AI's own words: who it's for",
      width: 300,
      valueGetter: params =>
        (params.row as TaggedCreative).observation?.targetPersona ||
        (params.row as TaggedCreative).error_message ||
        '',
      renderCell: params => (
        <TagCell text={String(params.value || '')} edited={false} muted />
      ),
    },
  ];

  const selected = rows.find(r => r.creative_key === selectedKey) || null;
  const unmapped = rows.filter(r => r.status === 'done' && !r.mapped).length;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Tagged creatives
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B' }}>
          Click a creative to see what the AI saw and correct any tag. Persona
          and selling-point columns stay empty until those lists are approved
          and applied.
          {unmapped > 0
            ? ` ${unmapped} creatives are waiting for "Apply approved lists".`
            : ''}
        </Typography>
      </Box>
      <Box sx={{ height: 640 }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          getRowId={row => (row as TaggedCreative).creative_key}
          density="compact"
          rowHeight={52}
          disableRowSelectionOnClick
          onRowClick={params => setSelectedKey(String(params.id))}
          initialState={{
            sorting: { sortModel: [{ field: 'spend', sort: 'desc' }] },
          }}
          slots={{ toolbar: Toolbar }}
          sx={{ border: 0, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>
      <TagEditorDialog
        // A fresh dialog per creative, so one creative's error doesn't show on the next.
        key={selected?.creative_key}
        creative={selected}
        vocabulary={vocabulary}
        onClose={() => setSelectedKey(null)}
        onSetTag={onSetTag}
      />
    </Paper>
  );
}
